import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  input,
} from '@angular/core';

import { ReportFmtPipe, ReportTPipe } from '../report-i18n.pipe';
import { ReportRevealDirective } from '../report-reveal.directive';
import { BandScaleSegment, ReportLevel, SubjectReportView } from '../report.model';

/**
 * Nom du plan associé à un palier, pour les libellés d'habillage.
 *
 * Le palier lui-même porte son intitulé et son plan, traduits par l'API. Ce
 * qui reste ici est le *texte de la maquette* — la description « ce que ce plan
 * veut dire » — qui n'existe que dans le bundle du rapport et qui y est classée
 * par nom de plan. Le vocabulaire visuel du palier (`ReportLevel`) est la seule
 * clé stable dont on dispose pour la retrouver.
 */
const PLAN_KEY: Record<ReportLevel, string> = {
  weak: 'remedial',
  average: 'improvement',
  good: 'development',
  excellent: 'gifted',
};

/** Un palier prêt à afficher : classes, délais et variables de libellé. */
interface BandView {
  code: string;
  /** Intitulé du plan, déjà localisé par l'API — jamais repassé au moteur i18n. */
  planLabel: string;
  width: number;
  railClass: string;
  railDelay: number;
  bandClass: string;
  bandDelay: number;
  active: boolean;
  descriptionKey: string;
  rangeVars: Record<string, unknown>;
}

/**
 * Section « barème des niveaux » d'une matière.
 *
 * **Piloté par les données, pas par la maquette.** Le document d'origine
 * codait quatre paliers en dur — 37 / 24 / 19 / 21 % de largeur et un curseur
 * à 43 % — parce qu'il décrivait un seul résultat. Les paliers sont
 * paramétrables en base : leur nombre, leurs bornes et leurs intitulés
 * arrivent maintenant dans `bandScale`, et les largeurs du rail sont la part
 * de l'échelle que chacun occupe.
 *
 * Le curseur se place au **pourcentage du score du test**, jamais à celui de
 * l'acquisition : c'est le score qui donne le palier.
 */
@Component({
  selector: 's2e-report-levels',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReportFmtPipe, ReportTPipe, ReportRevealDirective],
  templateUrl: './subject-levels.section.html',
  host: { '[style.display]': "'contents'" },
})
export class SubjectLevelsSection {
  readonly subject = input.required<SubjectReportView>();

  protected readonly bands = computed<BandView[]>(() =>
    this.subject().bandScale.map((segment, index) => this.toView(segment, index)),
  );

  private toView(segment: BandScaleSegment, index: number): BandView {
    return {
      code: segment.code,
      planLabel: segment.planLabel,
      width: segment.width,
      /* Les classes sont assemblées ici plutôt que par interpolation dans le
         gabarit : le palier atteint ajoute une classe conditionnelle, et
         mélanger interpolation et liaison de classe sur le même élément est
         précisément le cas où l'ordre d'application n'est pas garanti. */
      railClass: `level-rail__fill level-rail__fill--${segment.level} reveal reveal--scale-x`,
      railDelay: index * 100,
      bandClass:
        `level-band level-band--${segment.level}` +
        (segment.active ? ' level-band--active' : '') +
        ' reveal reveal--fade-up',
      bandDelay: index * 80,
      active: segment.active,
      descriptionKey: `levelScale.planDescriptions.${PLAN_KEY[segment.level]}`,
      /* Forme `{ n }` : les bornes sont mises en forme comme des nombres, le
         signe pour cent appartenant déjà au libellé `levelScale.range`. */
      rangeVars: {
        min: { n: segment.minPercentage },
        max: { n: segment.maxPercentage },
      },
    };
  }
}
